import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiCall } from "services/api";
import { downloadUrl } from "utils/linkDownload";
import { paramParsify } from "utils/queryParams";
import storage from "services/storage";

export const useGetTotalSharesScriptScriptless = ({ id = 1 } = {}) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/shares`,
          queryParams,
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id, queryParams],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [String(queryParams?.date), id]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetSharesDistributionBy = ({ by, id = 1 } = {}) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId, by: passedBy } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/shareholders/distribution`,
          queryParams: { ...queryParams, by: passedBy || by },
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [by, id, queryParams],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [String(queryParams?.date), id]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetShareholderList = ({
  id = 1,
  sortBy,
  enableAutoFetchByParams,
  isBlockedStatus = false,
} = {}) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async ({ id: passedId } = {}) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${passedId || id}/shareholders`,
          queryParams: {
            ...(isBlockedStatus
              ? {
                  status: [2, 3, 4],
                  sort_by: "ownership",
                }
              : []),
            ...(enableAutoFetchByParams && isBlockedStatus
              ? { ...queryParams }
              : {}),
            ...(enableAutoFetchByParams && !isBlockedStatus
              ? { date: queryParams?.date }
              : {}),
            ...(Boolean(sortBy) ? { sort_by: sortBy } : {}),
            limit: 10,
          },
          method: "GET",
        });
        setResponse(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [queryParams, id, isBlockedStatus, enableAutoFetchByParams, sortBy],
  );

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchByParams && isBlockedStatus
      ? [
          String(queryParams?.page),
          String(queryParams?.status),
          String(queryParams?.sort_by),
          String(queryParams?.sort_order),
          String(queryParams?.search),
          String(queryParams?.date),
        ]
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchByParams && !isBlockedStatus
      ? [String(queryParams?.date)]
      : []),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useExportShareholders = ({ onSuccess, onError } = {}) => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const queryParams = paramParsify(location?.search);
  const companyEffectCode = storage.getItem("effectCode");

  const exportFn = useCallback(
    async ({ id, type, effectiveDate } = {}) => {
      setLoading(true);
      try {
        const date =
          queryParams?.date ||
          effectiveDate ||
          dayjs(Date.now()).format("YYYY-MM-DD");
        const response = await apiCall({
          url: `/v1/company/${id}/shareholders-shares/export?type=${type}&date=${date}`,
          method: "GET",
          responseType: "blob",
        });

        if (response) {
          downloadUrl(
            response,
            `${companyEffectCode} - ${dayjs(date).format("DDMMYYYY")}.${type}`,
          );
        }
      } catch (error) {
        onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [companyEffectCode, onError, queryParams?.date],
  );

  return { exportFn, loading };
};
